import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import * as actions from '../../actions';
import Imprint from './Imprint';
import { getTranslationKey } from '../../modules/utils/helpers';
import Privacy from './Privacy';
import { FaPlus } from 'react-icons/fa';
import theme from '../../assets/css/theme';
import { isValidDomain, toPunycode } from '../../helpers/idn';

const NewDomain = (props: {
  auth: any;
  setUpgradeAccountOpenState: any;
  availableRules?: any;
  createDomain: any;
  notify: any;
}) => {
  const { auth, setUpgradeAccountOpenState, availableRules = {}, createDomain, notify } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [hostname, setHostname] = useState('');
  const [hostnameError, setHostnameError] = useState(false);
  const [imprint, setImprint] = useState(null);
  const [privacy, setPrivacy] = useState(null);

  const toggle = useCallback(() => setIsOpen((p) => !p), []);
  const freePlan = auth.type === 'SB' && auth.subscription.plan === 'free'; // eslint-disable-line
  const noDomainsLeft = availableRules.domainsLeft === 0; // eslint-disable-line

  const onChangeDomain = useCallback((e: { target: { value: any } }) => {
    const next = e.target.value;
    setHostname(next);
    setHostnameError(!isValidDomain(next));
  }, []);

  const onSelectedImprintChanged = useCallback((val: any) => setImprint(val), []);
  const onSelectedPrivacyChanged = useCallback((val: any) => setPrivacy(val), []);

  const isHostnameValid = useMemo(() => isValidDomain(hostname), [hostname]);
  const canCreate = isHostnameValid && !!imprint && !!privacy;

  const handleCreate = useCallback(() => {
    const error = !isValidDomain(hostname);
    setHostnameError(error);
    if (error) return;

    createDomain(
      {
        hostname: toPunycode(hostname.trim().toLowerCase()),
        imprint,
        privacy
      },
      notify
    );

    setIsOpen(false);
    setHostname('');
    setHostnameError(true);
    setImprint(null);
    setPrivacy(null);
  }, [createDomain, hostname, imprint, privacy, notify]);

  const handleKeyDown = useCallback(
    (e: { key: string; preventDefault: () => void }) => {
      if (e.key === 'Enter' && canCreate) {
        e.preventDefault();
        handleCreate();
      }
    },
    [canCreate, handleCreate]
  );

  return (
    <div className="inline">
      <button
        className="btn btn-light w-100-p create-new-button"
        onClick={() => {
          if (freePlan || noDomainsLeft) {
            return setUpgradeAccountOpenState({
              key: 'upgradeModal',
              state: true,
              desiredAction: 'domains',
              modalData: { addon: 'domains' }
            });
          }
          toggle();
        }}
      >
        <FaPlus
          data-cy="AddSubdomain"
          size={15}
          color={theme.commonColors.second}
          className="plus-icon mr-2"
        />
        {getTranslationKey('customDomains.newDomain')}
      </button>
      <Modal centered isOpen={isOpen} toggle={toggle} wrapClassName="modal-primary" size="default">
        <ModalHeader toggle={toggle}>{getTranslationKey('customDomains.newDomain')}</ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-lg-12">
              <div className="form-group">
                <b>{getTranslationKey('customDomains.yourSubDomain')}</b>
                <input
                  type="text"
                  className={`form-control ${hostnameError ? 'is-invalid' : ''}`}
                  placeholder={getTranslationKey('customDomains.placeholderNewDomain')}
                  value={hostname}
                  onChange={onChangeDomain}
                  onKeyDown={handleKeyDown}
                />
                {hostnameError ? (
                  <div className="invalid-feedback" style={{ display: 'block' }}>
                    {getTranslationKey('customDomains.invalidDomain')}
                  </div>
                ) : null}
              </div>
              <b>{getTranslationKey('customDomains.imprint')}</b>
              <Imprint onSelectedImprintChanged={onSelectedImprintChanged} />
              <b>{getTranslationKey('customDomains.privacy')}</b>
              <Privacy onSelectedPrivacyChanged={onSelectedPrivacyChanged} />
              {auth.type === 'SB' && (
                <a
                  href="https://support.scrappbook.de/de/articles/61600"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {getTranslationKey('customDomains.howTo')}
                </a>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-danger pull-left" onClick={toggle}>
            {getTranslationKey('abort')}
          </button>
          <button className="btn btn-primary" disabled={!canCreate} onClick={handleCreate}>
            {getTranslationKey('create')}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

// @ts-ignore
function mapStateToProps({ auth, notify, accessRules: { availableRules } }) {
  return { auth, notify, availableRules };
}
export default connect(mapStateToProps, actions)(NewDomain);
